import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, LogIn, ChevronRight } from "lucide-react";
import { NAV_LINKS, BRAND } from "../mock";
import { Button } from "./ui/button";
import api from "../lib/api";

const RESOURCES = [
  { l: "Current Students", to: "/portal/student" },
  { l: "Staff", to: "/admin/login" },
  { l: "Partners / Agents", to: "/admin/login" },
  { l: "Alumni", to: "/verify" },
  { l: "Online Courses (ODL)", to: "/odl-courses" },
  { l: "Verification", to: "/verify" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState({ phone: "", email: "" });
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/public/header-contact")
      .then(({ data }) => setContact({ phone: data.phone || "", email: data.email || "" }))
      .catch(() => {});
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  const phone = contact.phone || BRAND.phone;
  const email = contact.email;

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-[#004030] text-white/90 text-[13px]">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="tracking-wide">{BRAND.legal} · Kuala Lumpur, Malaysia</span>
          <div className="flex items-center gap-5">
            <a href={`tel:${phone}`} data-testid="header-phone" className="flex items-center gap-1.5 hover:text-[#e0c05a] transition-colors">
              <Phone size={13} /> {phone}
            </a>
            {email && (
              <a href={`mailto:${email}`} data-testid="header-email" className="flex items-center gap-1.5 hover:text-[#e0c05a] transition-colors">
                <Mail size={13} /> {email}
              </a>
            )}
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_6px_30px_-12px_rgba(0,64,48,0.3)]" : "bg-white"}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-6">
          <div className="flex items-center justify-between h-[70px]">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={BRAND.logo} alt="GDIH Elite Education" className="h-12 w-auto transition-transform duration-300 group-hover:scale-105" />
              <span className="hidden sm:flex flex-col leading-tight">
                <span className="font-display text-[#004030] font-bold text-lg">GDIH Elite</span>
                <span className="text-[10px] tracking-[0.2em] text-[#c9a227] font-semibold uppercase">Education</span>
              </span>
            </Link>

            <div className="flex items-center gap-3">
              <Link to="/admin/login?portal=student" className="hidden sm:block">
                <Button data-testid="apply-now-btn" className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full px-6 h-11 font-bold shadow-md hover:shadow-lg transition-all">
                  Apply Now
                </Button>
              </Link>
              <button
                onClick={() => setOpen(true)}
                data-testid="nav-hamburger"
                className="flex items-center gap-2 px-3.5 h-11 rounded-full border border-slate-200 text-[#004030] hover:bg-[#004030] hover:text-white hover:border-[#004030] transition-colors font-medium"
                aria-label="Open menu"
              >
                <Menu size={20} /> <span className="hidden sm:inline text-sm">Menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Slide-in drawer */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-[#012a1e]/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="nav-backdrop" />
        <aside
          data-testid="nav-drawer"
          className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-5 h-[70px] border-b border-slate-100 shrink-0">
            <span className="font-display text-[#004030] font-bold text-lg">Menu</span>
            <button onClick={() => setOpen(false)} data-testid="nav-close" className="w-10 h-10 rounded-full grid place-items-center text-[#004030] hover:bg-slate-100"><X size={22} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <p className="px-3 text-[11px] font-bold uppercase tracking-wider text-[#c9a227]">Explore</p>
            <nav className="mt-1 mb-4">
              {NAV_LINKS.map((link) => (
                <Link key={link.to} to={link.to}
                  className={`flex items-center justify-between py-3 px-3 rounded-lg text-[15px] font-medium ${location.pathname === link.to ? "text-[#004030] bg-[#f0f6f3]" : "text-slate-700 hover:bg-slate-50"}`}>
                  {link.label} <ChevronRight size={16} className="text-slate-300" />
                </Link>
              ))}
            </nav>

            <p className="px-3 text-[11px] font-bold uppercase tracking-wider text-[#c9a227]" data-testid="resources-menu">Resources For</p>
            <nav className="mt-1">
              {RESOURCES.map((r) => (
                <Link key={r.l} to={r.to} data-testid={`resource-${r.l.split(" ")[0].toLowerCase()}`}
                  className="flex items-center justify-between py-2.5 px-3 rounded-lg text-[14px] text-slate-600 hover:text-[#004030] hover:bg-slate-50">
                  {r.l} <ChevronRight size={15} className="text-slate-300" />
                </Link>
              ))}
            </nav>
          </div>

          <div className="p-4 border-t border-slate-100 space-y-2.5 shrink-0">
            <Link to="/admin/login?portal=student"><Button data-testid="apply-now-drawer" className="w-full bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 font-bold">Apply Now</Button></Link>
            <Link to="/contact"><Button variant="outline" className="w-full border-[#004030] text-[#004030] rounded-full h-12 font-semibold">Book Free Consult</Button></Link>
            <Link to="/admin/login" className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-[#004030] pt-1"><LogIn size={15} /> Staff / Agent Login</Link>
          </div>
        </aside>
      </div>
    </>
  );
}
